import React, { useMemo, useState } from 'react'
import { Button, Modal } from 'antd'
import { useSelector } from 'react-redux'
import { DownloadOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { contextCompanyIdSelector } from '../../../../redux/context/company/selectors'
import { VoucherQueryParams, AccountCondition } from '../../../../pages/reporting/vouchers/types'
import { ReportTableRow } from '../../../../components/Table/types'
import { Column } from '../../../../redux/context/periodGroups/hooks'
import { getRequestParams, getStatementRowAccounts } from '../../utils/voucherUtils'
import ToolBar from '../../../../components/Misc/ToolBar'
import { useXlsxDownload } from '../../queries/useXlsxDownload'
import { StatementColumn } from '../../../financialStatements'
import { EnhancedStatementRow } from '../../../financialStatements/utils/dataEnhancement'
import { DimensionTree } from '../../../dimension/components/DimensionTree'
import DimensionName from '../../../dimension/components/DimensionName'
import VoucherView from '../VoucherTable/VoucherView'

export interface VoucherModalProps {
  row: ReportTableRow | EnhancedStatementRow
  column: Column | StatementColumn
  open: boolean
  onClose: () => void
}

const VoucherModal: React.FC<VoucherModalProps> = ({ row, column, open, onClose }) => {
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)!

  // Extract row info (used for title, accounts, dimensions)
  const rowInfo = useMemo(() => {
    let accountCode: number | undefined
    let dimensionId: string | undefined

    // Handle EnhancedStatementRow
    if ('_sourceAccountCode' in row && typeof row._sourceAccountCode === 'number') {
      accountCode = row._sourceAccountCode
    }
    if ('dimensionId' in row && typeof row.dimensionId === 'string') {
      dimensionId = row.dimensionId
    }

    // Handle ReportTableRow
    if ('code' in row && typeof row.code === 'number') {
      accountCode = row.code
    }

    // Get name
    const name = 'name' in row ? row.name : (row as any).title

    return { accountCode, name, dimensionId }
  }, [row])

  // Resolve accounts from row
  const rowAccounts = useMemo((): AccountCondition[] => {
    if ('_sourceAccountCode' in row && typeof row._sourceAccountCode === 'number') {
      return [row._sourceAccountCode]
    }
    if ('_sourceStatementRowId' in row && typeof row._sourceStatementRowId === 'number') {
      return getStatementRowAccounts(row._sourceStatementRowId)
    }
    if ('code' in row && typeof row.code === 'number') {
      return [row.code]
    }
    if ('id' in row) {
      return getStatementRowAccounts(row.id)
    }
    return []
  }, [row])

  // Generate modal title
  const title = useMemo(() => {
    const { accountCode, name, dimensionId } = rowInfo
    const displayTitle = accountCode ? `${accountCode} - ${name || ''}` : name || ''
    const vouchersLabel = t('global:vouchers')

    if (dimensionId) {
      return (
        <span>
          {vouchersLabel}: {displayTitle} &gt; <DimensionName dimensionId={dimensionId} />
        </span>
      )
    }
    return `${vouchersLabel}: ${displayTitle}`
  }, [rowInfo, t])

  // Resolve dimension query from row
  const dimensionsParam = useMemo(() => {
    if (rowInfo.dimensionId) {
      const query = DimensionTree.queryBuilder([[rowInfo.dimensionId]])
      return JSON.stringify([query])
    }
    return undefined
  }, [rowInfo.dimensionId])

  // Initial params - accounts + date range from column
  const initialParams = useMemo((): Partial<VoucherQueryParams> => {
    const params: Partial<VoucherQueryParams> = {
      accounts: rowAccounts
    }

    if (column.periodGroup?.periods?.length) {
      const { periods } = column.periodGroup
      const [firstPeriod] = periods
      const lastPeriod = periods[periods.length - 1]

      params.from = dayjs(new Date(firstPeriod.year, firstPeriod.month - 1, 1))
      params.until = dayjs(new Date(lastPeriod.year, lastPeriod.month, 0))
    } else if (column.month && column.year) {
      params.from = dayjs(new Date(column.year, column.month - 1, 1))
      params.until = dayjs(new Date(column.year, column.month, 0))
    }

    return params
  }, [rowAccounts, column])

  // Track current grid filter params for Excel export
  const [exportParams, setExportParams] = useState<Partial<VoucherQueryParams>>(initialParams)

  const downloadParams = useMemo(() => {
    return getRequestParams(exportParams)
  }, [exportParams])

  const { download: downloadVoucherXlsx, isDownloading: fileLoading } = useXlsxDownload(
    downloadParams,
    companyId,
    dimensionsParam
  )

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={onClose}
      width="90vw"
      style={{ maxWidth: 1600 }}
      destroyOnHidden
      footer={null}
    >
      <div>
        <ToolBar justifyContent="right">
          <Button
            type="default"
            icon={<DownloadOutlined />}
            loading={fileLoading}
            onClick={() => downloadVoucherXlsx()}
          >
            Excel
          </Button>
        </ToolBar>
        <VoucherView
          initialParams={initialParams}
          dimensions={dimensionsParam}
          height={600}
          onExportParamsChange={setExportParams}
        />
      </div>
    </Modal>
  )
}

export default VoucherModal
